import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./KPIDashboardScreen.css";
import "./MissionReady.css";
import "./TimeTrackingV2Screen.css";
import { CPDropdown } from "./CPDropdown";
import { CPInput } from "./CPInput";
import { CPTextarea } from "./CPTextarea";
import { SessionMaterialsPanel } from "./SessionMaterialsPanel";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { DesktopBackLink } from "./navigation/ScreenBackButton";
import { DeskCol, DeskGrid } from "./ops/DeskCols";
import "./ops/opsDesktop.css";
import { supabase } from "../supabase";
import { loadScopedActiveProjects } from "../helpers/operationalQueryAdapters";
import { scopeOperationalActiveSessionsForActor } from "../helpers/scopedQueries";
import { getActiveProjects } from "../helpers/projectVisibility";
import {
  assertProjectOperationallyWritable,
  CP_CLIENT_ARCHIVED_EVENT,
  CP_CLIENT_RESTORED_EVENT,
  OPERATIONAL_LOCK_USER_MESSAGE,
} from "../helpers/operationalLifecycle";
import {
  getScopedCachedProjects,
  saveScopedCachedProjects,
  getScopedPendingTimelogs,
  persistScopedPendingTimelogs,
  resolveCacheOrganizationId,
  stampPendingTimelogForQueue,
} from "../helpers/offlineCacheScope";
import {
  clearPendingActiveSessionDelete,
  enqueuePendingActiveSessionDelete,
  upsertPendingTimelogBySessionId,
} from "../helpers/syncRecovery";
import { isOffline, subscribeConnectivity } from "../helpers/connectivity";
import {
  buildProgressReports,
  reportSessionTaskProgress,
} from "../helpers/tasks/taskProgress";
import {
  SessionTaskProgressNotice,
  SessionTaskProgressPanel,
} from "./tasks/SessionTaskProgress";
import { flushSessionMaterialSync, getSessionMaterials } from "../helpers/sessionMaterials";
import { applyQuoteLineLinkToTimelogRow } from "../helpers/projectLabourLedger";
import {
  FINALIZE_TRACE_PATH,
  finalizeRequiredSessionMaterials,
  runCriticalTimelogFinalize,
  runOptionalFinalizeQuoteLink,
} from "../helpers/sessionFinalizePipeline";
import { fetchAndCacheMaterialsCatalog } from "../helpers/materialsCatalog";
import { toNullableUuid } from "../helpers/uuidFields";
import { orgScopedInsert } from "../helpers/orgQueryScope";
import { blockDemoSave, shouldSkipSupabase } from "../helpers/guestMode";
import { getMaterialRelationName } from "../helpers/materialVisibility";
import {
  buildEmployeeSnapshot,
  resolveTimelogEmployeeSnapshot,
  snapshotToTimelogFields,
} from "../helpers/employeeSnapshot";
import { formatCurrency } from "../helpers/estimateTotals";
import { TIME_LOG_PAYROLL_TYPE_OPTIONS } from "../helpers/payrollHelpers";
import { loadPayrollTypeSessionOptions } from "../helpers/catalogs/payrollTypeCatalog";
import {
  NO_WORK_TYPES_MESSAGE,
  SELECT_WORK_TYPE_MESSAGE,
  loadWorkTypesForSession,
  readInitialSessionDefaultWorkType,
  readInitialSessionWorkTypeOptions,
  resolveDefaultWorkTypeName,
  resolveSessionWorkType,
  workTypesToSessionOptions,
} from "../helpers/catalogs/workTypeCatalog";
import { traceTimelogWrite } from "../helpers/timelogWriteTrace";
import {
  clearLocalActiveSession,
  getLocalActiveSession,
  saveLocalActiveSession,
} from "../helpers/activeSession";
import {
  dispatchSessionStateChanged,
  APP_SESSION_STATE_CHANGED_EVENT,
  SESSION_STATE_ACTION,
} from "../helpers/appSessionStateEvents";
import { startBreak, stopBreak } from "../helpers/sessionBreakActions";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import { useStartSessionVoiceMvp } from "../ashley/startSession/useStartSessionVoiceMvp";
import { VoiceGuidedFormProvider } from "../ashley/forms/VoiceGuidedFormContext";
import { VoiceGuidedFormOverlay } from "../ashley/forms/VoiceGuidedFormOverlay";
import {
  clearReviewSession,
  getReviewSession,
  saveReviewSession,
} from "../helpers/reviewSession";
import { devLog } from "../helpers/devLog";
import {
  combineLocalDateAndTime,
  computeSessionDurationSeconds,
  formatTimeInputValue,
  toLocalDateInputValue,
} from "../helpers/dateTimeHelpers";

function getPendingTimeLogs(currentUser) {
  return getScopedPendingTimelogs(currentUser);
}

function savePendingTimeLogs(logs, currentUser) {
  persistScopedPendingTimelogs(logs, currentUser);
}

function formatSessionDateTime(dateInput) {
  const date = new Date(dateInput);
  return `${date.toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })} @ ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function formatTotalHoursDisplay(totalHours) {
  const totalMinutes = Math.floor(Number(totalHours || 0) * 60);
  if (totalMinutes >= 60) {
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hrs} hr${hrs !== 1 ? "s" : ""} ${mins} min`;
  }
  return `${totalMinutes} min`;
}

function pickLastProjectId(activeProjects) {
  const lastProjectId = localStorage.getItem("lastProjectId");

  if (
    lastProjectId &&
    activeProjects.some((project) => project.project_id === lastProjectId)
  ) {
    return lastProjectId;
  }

  return "";
}

/* Action-row icons — presentational only, no behavior. */
const SW_ICON = {
  break: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  ),
  resume: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 4.5v15l13-7.5-13-7.5Z" />
    </svg>
  ),
  stop: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="5" width="14" height="14" rx="2" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  ),
  project: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a1 1 0 0 1 1-1h4l2 2h10a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7Z" />
    </svg>
  ),
  client: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="3" width="9" height="18" rx="1" />
      <path d="M14 8h5a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-5" />
      <path d="M8.5 7.5h.01M11.5 7.5h.01M8.5 11h.01M11.5 11h.01M8.5 14.5h.01M11.5 14.5h.01" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17M8 3v3.5M16 3v3.5" />
    </svg>
  ),
  materials: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3.5 20 8v8l-8 4.5L4 16V8Z" />
      <path d="M4 8l8 4.5L20 8M12 12.5V21" />
    </svg>
  ),
  classification: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" />
    </svg>
  ),
  payroll: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18M7 14.5h4" />
    </svg>
  ),
  rate: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v10M14.5 9.3c0-1-1-1.8-2.5-1.8s-2.5.9-2.5 2 1 1.6 2.5 1.9 2.5.9 2.5 2-1.1 2-2.5 2-2.5-.7-2.5-1.8" />
    </svg>
  ),
  notes: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3.5h9l3 3v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1Z" />
      <path d="M9 10h6M9 14h6M9 18h3" />
    </svg>
  ),
  actions: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 6 6 6-6 6" />
    </svg>
  ),
};

export function TimeTrackingV2Screen({
  onBack,
  session,
  currentUser,
  directReportIds = [],
  voiceWorkflow = null,
  onVoiceWorkflowConsumed,
}) {
  const isDesktop = useIsDesktopViewport();
  const [activeSession, setActiveSession] = useState(null);
  const [openMaterialsPicker, setOpenMaterialsPicker] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedPayrollType, setSelectedPayrollType] = useState("Standard");
  const [payrollTypeOptions, setPayrollTypeOptions] = useState(
    () => [...TIME_LOG_PAYROLL_TYPE_OPTIONS],
  );
  const [resolvedHourlyRate, setResolvedHourlyRate] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const [workCompleted, setWorkCompleted] = useState("");

  // `category` holds only an explicit employee choice ("" = none). The value
  // shown and saved is resolvedWorkType: explicit choice, else org default.
  const [category, setCategory] = useState("");
  const [workTypeOptions, setWorkTypeOptions] = useState(() =>
    readInitialSessionWorkTypeOptions(currentUser),
  );
  const [defaultWorkTypeName, setDefaultWorkTypeName] = useState(() =>
    readInitialSessionDefaultWorkType(currentUser),
  );
  const [workTypeError, setWorkTypeError] = useState("");
  const resolvedWorkType = resolveSessionWorkType({
    explicitChoice: category,
    options: workTypeOptions,
    defaultName: defaultWorkTypeName,
  });

  function handleWorkTypeChange(value) {
    setCategory(value);
    setWorkTypeError("");
  }

  /** Blocks submit when no valid Work Type resolves; returns true when OK. */
  function ensureWorkTypeForSubmit() {
    if (resolvedWorkType) {
      return true;
    }
    setWorkTypeError(
      workTypeOptions.length ? SELECT_WORK_TYPE_MESSAGE : NO_WORK_TYPES_MESSAGE,
    );
    return false;
  }

  const [notes, setNotes] = useState("");
  const [lastSessionId, setLastSessionId] = useState(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [materialRetryPending, setMaterialRetryPending] = useState(false);
  const [finalizedTimeLogId, setFinalizedTimeLogId] = useState(null);
  const [showStartTimeEditor, setShowStartTimeEditor] = useState(false);

  const [editedStartDate, setEditedStartDate] = useState("");
  const [editedStartTime, setEditedStartTime] = useState("");
  const [showActiveProjectEditor, setShowActiveProjectEditor] = useState(false);

  const [editedProjectId, setEditedProjectId] = useState("");

  // Review / Closeout: entered by tapping "Stop Session" while the session
  // remains fully active (no stop/finalize write happens until Submit).
  const [showCloseoutScreen, setShowCloseoutScreen] = useState(false);
  const [isSubmittingSession, setIsSubmittingSession] = useState(false);

  // V1 Task Duration & Progress: optional per-task progress reported at Stop
  // Session. Kept apart from the time session: it is sent only after the
  // labour time log is saved, and its failure never affects that record.
  const [progressTasks, setProgressTasks] = useState([]);
  const [progressDrafts, setProgressDrafts] = useState({});
  const [progressOutcome, setProgressOutcome] = useState({ status: "idle" });
  const [connectivityOffline, setConnectivityOffline] = useState(() => isOffline());
  const progressSessionRef = useRef(null);
  useEffect(() => subscribeConnectivity(() => setConnectivityOffline(isOffline())), []);
  const activeSessionId = activeSession?.session_id || null;
  useEffect(() => {
    const id = activeSessionId;
    if (id && progressSessionRef.current !== id) {
      progressSessionRef.current = id;
      setProgressDrafts({});
      setProgressTasks([]);
      setProgressOutcome({ status: "idle" });
    }
  }, [activeSessionId]);

  async function sendTaskProgress(sessionId, reports, tasksForTitles = progressTasks) {
    if (!reports.length) return;
    const titleOf = (taskId) => tasksForTitles.find((t) => t.project_task_id === taskId)?.title || "Task";
    setProgressOutcome({ status: "saving", sessionId, reports });
    setProgressOutcome(await reportSessionTaskProgress(currentUser, { sessionId, reports, titleOf }));
  }

  function startTaskProgressSubmit(sessionId) {
    try {
      const { reports } = buildProgressReports(progressTasks, progressDrafts);
      if (!reports.length) return;
      sendTaskProgress(sessionId, reports).catch((e) => console.error("TASK PROGRESS ERROR:", e));
    } catch (progressError) {
      // Never let progress reporting disturb the saved session.
      console.error("TASK PROGRESS ERROR:", progressError);
    }
  }

  const [reviewTimelog, setReviewTimelog] = useState(null);
  // Crash/reload recovery only: set when a previous Submit already wrote the
  // stop (time_logs insert + active_sessions cleanup) but never confirmed the
  // finalize (work summary + material durable commit) step. See mount effect.
  const [showPostStopRecovery, setShowPostStopRecovery] = useState(false);

  const [showBreakEditor, setShowBreakEditor] = useState(false);

  const [reviewBreakMinutes, setReviewBreakMinutes] = useState("");
  const [voiceSessionEnabled, setVoiceSessionEnabled] = useState(false);
  const selectedProject = projects.find(
  (p) => p.project_id === selectedProjectId,
);
  const activeProject = projects.find(
    (p) => p.project_id === activeSession?.project_id,
  );
  devLog("CURRENT USER", currentUser);

  useEffect(() => {
    if (shouldSkipSupabase(currentUser)) {
      loadProjects();
      return;
    }

    loadActiveSession();
    loadProjects();
    fetchAndCacheMaterialsCatalog(supabase, {
      relation: getMaterialRelationName(currentUser),
      actor: currentUser,
    });

    const pendingReview = getReviewSession();

    if (pendingReview) {
      // The stop write already happened (time_logs insert + active_sessions
      // cleanup) in a previous Submit attempt that never reached
      // completeFinalizeUiTransition() — e.g. the app closed/reloaded mid
      // finalize. The active session is genuinely gone at this point, so we
      // do not attempt to resume Active Session here; we let the worker
      // finish submitting the work summary/materials for the already-frozen
      // record instead. See the postStopRecovery screen below.
      setReviewTimelog(pendingReview);

      setLastSessionId(pendingReview.session_id);

      setShowPostStopRecovery(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPayrollTypeOptions() {
      const options = await loadPayrollTypeSessionOptions(supabase, currentUser);
      if (!cancelled) {
        setPayrollTypeOptions(options);
      }
    }

    loadPayrollTypeOptions();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.organization_id, currentUser?.supabase_user_id]);

  useEffect(() => {
    let cancelled = false;

    async function loadWorkTypeOptions() {
      const rows = await loadWorkTypesForSession(supabase, currentUser);
      if (!cancelled) {
        setWorkTypeOptions(workTypesToSessionOptions(rows));
        setDefaultWorkTypeName(resolveDefaultWorkTypeName(rows));
      }
    }

    loadWorkTypeOptions();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.organization_id, currentUser?.supabase_user_id]);

  useEffect(() => {
    let cancelled = false;

    async function loadResolvedHourlyRate() {
      setResolvedHourlyRate(null);

      const employeeSnapshot = await resolveTimelogEmployeeSnapshot(currentUser, {
        supabase,
        payrollType: selectedPayrollType,
        supabaseUserId: session?.user?.id,
      });

      if (!cancelled) {
        setResolvedHourlyRate(employeeSnapshot.hourly_rate || 0);
      }
    }

    loadResolvedHourlyRate();

    return () => {
      cancelled = true;
    };
  }, [
    selectedPayrollType,
    currentUser?.trade_id,
    currentUser?.trade_name,
    currentUser?.classification_name,
    currentUser?.hourly_rate,
    session?.user?.id,
  ]);

  useEffect(() => {
    function handleProjectArchived(event) {
      const projectId = event.detail?.projectId;

      if (!projectId) {
        return;
      }

      setProjects((current) =>
        current.filter((project) => project.project_id !== projectId),
      );

      setSelectedProjectId((currentId) =>
        currentId === projectId ? "" : currentId,
      );
      setEditedProjectId((currentId) =>
        currentId === projectId ? "" : currentId,
      );
    }

    function handleClientArchived(event) {
      const clientId = event.detail?.clientId;
      if (!clientId) {
        return;
      }

      setProjects((current) => {
        const removedIds = new Set(
          current
            .filter((project) => project.client_id === clientId)
            .map((project) => project.project_id),
        );

        queueMicrotask(() => {
          setSelectedProjectId((id) => (removedIds.has(id) ? "" : id));
          setEditedProjectId((id) => (removedIds.has(id) ? "" : id));
          try {
            const lastProjectId = localStorage.getItem("lastProjectId");
            if (lastProjectId && removedIds.has(lastProjectId)) {
              localStorage.removeItem("lastProjectId");
            }
          } catch {
            // ignore
          }
        });

        return current.filter((project) => project.client_id !== clientId);
      });
    }

    function handleClientRestored() {
      // Screens reload project lists on navigation/focus; event is for cache drop.
    }

    window.addEventListener("cp-project-archived", handleProjectArchived);
    window.addEventListener(CP_CLIENT_ARCHIVED_EVENT, handleClientArchived);
    window.addEventListener(CP_CLIENT_RESTORED_EVENT, handleClientRestored);

    return () => {
      window.removeEventListener("cp-project-archived", handleProjectArchived);
      window.removeEventListener(CP_CLIENT_ARCHIVED_EVENT, handleClientArchived);
      window.removeEventListener(CP_CLIENT_RESTORED_EVENT, handleClientRestored);
    };
  }, []);

  useEffect(() => {
    if (!voiceWorkflow?.workflow) {
      return;
    }

    if (voiceWorkflow.workflow === "open_materials_picker") {
      if (!activeSession) {
        return;
      }

      setOpenMaterialsPicker(true);
      onVoiceWorkflowConsumed?.();
      return;
    }

    if (voiceWorkflow.workflow === "open_stop_session") {
      if (!activeSession) {
        return;
      }

      setShowCloseoutScreen(true);
      onVoiceWorkflowConsumed?.();
      return;
    }

    if (voiceWorkflow.workflow === "open_start_session") {
      onVoiceWorkflowConsumed?.();
      return;
    }

    if (voiceWorkflow.workflow === "start_session_mvp") {
      if (activeSession) {
        onVoiceWorkflowConsumed?.();
        return;
      }

      setVoiceSessionEnabled(true);
      onVoiceWorkflowConsumed?.();
    }
  }, [activeSession, onVoiceWorkflowConsumed, voiceWorkflow]);

  useEffect(() => {
    if (!activeSession?.session_start) return;

    const interval = setInterval(() => {
      const start = new Date(activeSession.session_start);
      const now = new Date();

      setElapsedSeconds(Math.floor((now.getTime() - start.getTime()) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [activeSession]);

  const handleHardwareBack = useCallback(() => {
    if (showSuccessScreen) {
      setShowSuccessScreen(false);
      onBack?.();
      return true;
    }

    if (showCloseoutScreen) {
      if (showActiveProjectEditor) {
        setShowActiveProjectEditor(false);
        return true;
      }

      if (showStartTimeEditor) {
        setShowStartTimeEditor(false);
        return true;
      }

      if (showBreakEditor) {
        setShowBreakEditor(false);
        return true;
      }

      // Return to Session: no rollback needed, since no stop/finalize write
      // has happened yet — the session has been active this whole time.
      setShowCloseoutScreen(false);
      return true;
    }

    if (showStartTimeEditor) {
      setShowStartTimeEditor(false);
      return true;
    }

    if (showActiveProjectEditor) {
      setShowActiveProjectEditor(false);
      return true;
    }

    if (showBreakEditor) {
      setShowBreakEditor(false);
      return true;
    }

    if (openMaterialsPicker) {
      setOpenMaterialsPicker(false);
      return true;
    }

    return false;
  }, [
    onBack,
    openMaterialsPicker,
    showActiveProjectEditor,
    showBreakEditor,
    showCloseoutScreen,
    showStartTimeEditor,
    showSuccessScreen,
  ]);

  useHardwareBackHandler(handleHardwareBack);

  async function loadActiveSession() {
    const userId = session?.user?.id;
    const localSession = getLocalActiveSession(userId);

    if (localSession) {
      setActiveSession(localSession);
      return;
    }

    if (!userId) return;

    let query = supabase
      .from("active_sessions")
      .select("*")
      .eq("supabase_user_id", userId)
      .maybeSingle();

    query = await scopeOperationalActiveSessionsForActor(
      query,
      supabase,
      currentUser,
    );

    const { data, error } = await query;

    if (error) {
      console.error("LOAD ACTIVE SESSION ERROR:", error);
      return;
    }

    if (data) {
      saveLocalActiveSession(data);
    }

    setActiveSession(data);
  }

  useEffect(() => {
    function handleSessionStateChanged() {
      loadActiveSession();
    }

    window.addEventListener(
      APP_SESSION_STATE_CHANGED_EVENT,
      handleSessionStateChanged,
    );

    return () => {
      window.removeEventListener(
        APP_SESSION_STATE_CHANGED_EVENT,
        handleSessionStateChanged,
      );
    };
  }, []);

  async function loadProjects() {
    const cachedProjects = getScopedCachedProjects(currentUser, directReportIds);

    if (cachedProjects?.length) {
      devLog("BOOTSTRAP PROJECT CACHE");

      const activeProjects = getActiveProjects(cachedProjects);
      setProjects(activeProjects);
      setSelectedProjectId((currentId) =>
        currentId || pickLastProjectId(activeProjects),
      );
    }
    const userId = session?.user?.id;

    if (currentUser?.isGuest) {
      setProjects([
        {
          project_id: "guest-1",
          project_name: "Highway 17 Soil Investigation",
          client_id: null,
        },
        {
          project_id: "guest-2",
          project_name: "Municipal Water Study",
          client_id: null,
        },
        {
          project_id: "guest-3",
          project_name: "Fleet Operations",
          client_id: null,
        },
        {
          project_id: "guest-4",
          project_name: "Smith Quarry Investigation",
          client_id: null,
        },
      ]);
      setSelectedProjectId("guest-1");
      return;
    }

    if (!userId) {
      return;
    }

    if (isOffline()) {
      const cachedProjects = getScopedCachedProjects(currentUser, directReportIds);

      if (cachedProjects?.length) {
        const activeProjects = getActiveProjects(cachedProjects);
        setProjects(activeProjects);
        setSelectedProjectId((currentId) =>
          currentId || pickLastProjectId(activeProjects),
        );
      }

      return;
    }

    devLog("LOAD PROJECTS", currentUser?.role, session?.user?.id);

    const projectSelect = `
  project_id,
  project_name,
  created_by,
  client_id,
  clients (
    client_name,
    contact_name,
    phone,
    notes
  )
`;

    try {
      const data = await loadScopedActiveProjects(
        supabase,
        currentUser,
        { directReportIds },
        projectSelect,
      );

      const activeProjects = getActiveProjects(data || []);

      saveScopedCachedProjects(activeProjects, currentUser, directReportIds);

      devLog("CACHED PROJECTS SAVED");

      setProjects(activeProjects);

      setSelectedProjectId((currentId) =>
        currentId || pickLastProjectId(activeProjects),
      );
    } catch (error) {
      console.error(error);

      const cachedProjects = getScopedCachedProjects(currentUser, directReportIds);

      // Empty cache is a miss. Do not replace an in-memory list with it.
      if (cachedProjects?.length) {
        devLog("USING CACHED PROJECTS");

        const activeProjects = getActiveProjects(cachedProjects);
        setProjects(activeProjects);
        setSelectedProjectId((currentId) =>
          currentId || pickLastProjectId(activeProjects),
        );
      }
    }
  }

  async function handleStartSession() {
    if (shouldSkipSupabase(currentUser)) {
      blockDemoSave(currentUser);
      return false;
    }

    if (activeSession) {
      alert("Active session already exists.");
      return false;
    }
    const selectedProject = projects.find(
      (p) => p.project_id === selectedProjectId,
    );
    localStorage.setItem("lastProjectId", selectedProjectId);

    if (!selectedProject) {
      alert("Select a project first.");
      return false;
    }

    try {
      assertProjectOperationallyWritable(selectedProject, selectedProject.clients);
    } catch (lockError) {
      alert(lockError?.message || OPERATIONAL_LOCK_USER_MESSAGE);
      return false;
    }

    const userId = session?.user?.id;

    if (!userId) return false;

    const employeeSnapshot = await resolveTimelogEmployeeSnapshot(currentUser, {
      supabase,
      payrollType: selectedPayrollType,
      supabaseUserId: userId,
    });

    if (employeeSnapshot.missingCustomRate) {
      alert("User Rate requires an hourly rate in User Management.");
      return false;
    }

    const payrollRate = employeeSnapshot.hourly_rate;

    devLog("PAYROLL RATE", payrollRate);
    const sessionId = crypto.randomUUID();

    const sessionPayload = {
      session_id: sessionId,
      supabase_user_id: userId,
      project_id: selectedProject.project_id,
      project_name: selectedProject.project_name,
      client_id: selectedProject.client_id || null,
      status: "active",
      session_start: new Date().toISOString(),
      break_active: false,
      break_seconds: 0,
      classification_name: employeeSnapshot.classification_name || "",
      payroll_type: employeeSnapshot.payroll_type,
      hourly_rate: payrollRate,
      trade_id: employeeSnapshot.trade_id,
      trade_name: employeeSnapshot.trade_name || "",
    };

    saveLocalActiveSession(sessionPayload);

    setActiveSession(sessionPayload);
    dispatchSessionStateChanged({ action: SESSION_STATE_ACTION.START_SESSION });

    if (isOffline()) {
      devLog("OFFLINE START SESSION");
      return true;
    }

    const { error } = await orgScopedInsert(
      supabase,
      "active_sessions",
      [sessionPayload],
      currentUser,
    );

    if (error) {
      devLog("OFFLINE START SESSION");
      return true;
    }

    loadActiveSession();
    return true;
  }

  const handleStartSessionRef = useRef(handleStartSession);
  handleStartSessionRef.current = handleStartSession;

  const handleVoiceSessionComplete = useCallback(() => {
    setVoiceSessionEnabled(false);
  }, []);

  const projectOptions = useMemo(
    () =>
      projects.map((project) => ({
        value: project.project_id,
        label: project.project_name,
      })),
    [projects],
  );

  const voiceMvp = useStartSessionVoiceMvp({
    enabled: voiceSessionEnabled,
    setSelectedProjectId,
    projectOptions,
    setSelectedPayrollType,
    payrollOptions: payrollTypeOptions,
    onStartSession: () => handleStartSessionRef.current(),
    onComplete: handleVoiceSessionComplete,
  });

  async function handleSaveStartTime() {
    if (!activeSession || !editedStartDate || !editedStartTime) {
      return;
    }

    const nextStart = combineLocalDateAndTime(editedStartDate, editedStartTime);

    if (!nextStart) {
      alert("Start date and start time are required.");
      return;
    }

    if (nextStart > new Date()) {
      alert("Start time cannot be in the future.");
      return;
    }

    const updatedSession = {
      ...activeSession,
      session_start: nextStart.toISOString(),
    };

    saveLocalActiveSession(updatedSession);
    setActiveSession(updatedSession);
    setShowStartTimeEditor(false);

    const { error } = await supabase
      .from("active_sessions")
      .update({
        session_start: nextStart.toISOString(),
      })
      .eq("session_id", activeSession.session_id);

    if (error) {
      devLog("OFFLINE CHANGE START TIME");
    }
  }
  async function handleStartBreak() {
    if (!activeSession) {
      return;
    }

    const { session } = await startBreak(activeSession);
    if (session) {
      setActiveSession(session);
    }
  }

  async function handleStopBreak() {
    if (!activeSession) {
      return;
    }

    const { session } = await stopBreak(activeSession);
    if (session) {
      setActiveSession(session);
    }
  }

  function completeFinalizeUiTransition() {
    clearReviewSession();
    setReviewTimelog(null);
    setShowPostStopRecovery(false);
    setWorkCompleted("");
    setCategory("");
    setWorkTypeError("");
    setNotes("");
    setMaterialRetryPending(false);
    setFinalizedTimeLogId(null);
    setIsSubmittingSession(false);
    setShowSuccessScreen(true);
  }

  function showMaterialRetryState() {
    setShowPostStopRecovery(false);
    setIsSubmittingSession(false);
    setMaterialRetryPending(true);
  }

  async function persistFinalizeMaterials(
    sessionId,
    materialLines,
    materialCommitIdempotencyKey,
    timeLogId = null,
    timelogForUsageDate = reviewTimelog,
  ) {
    const usageDate =
      timelogForUsageDate?.date_iso ||
      toLocalDateInputValue(timelogForUsageDate?.start_time || new Date());

    return finalizeRequiredSessionMaterials({
      sessionId,
      reviewTimelog: timelogForUsageDate,
      materialLines,
      materialCommitIdempotencyKey,
      enteredBy: session?.user?.id,
      usageDate,
      timeLogId,
      getPendingTimeLogs,
      savePendingTimeLogs,
      currentUser,
    });
  }

  async function handleRetryMaterialFinalize() {
    if (!lastSessionId) {
      return;
    }

    const materialLines = getSessionMaterials(lastSessionId);
    const pendingLogs = getPendingTimeLogs(currentUser);
    const existingLog = pendingLogs.find((log) => log.session_id === lastSessionId);
    const materialCommitIdempotencyKey =
      existingLog?.material_commit_idempotency_key || crypto.randomUUID();

    const result = await persistFinalizeMaterials(
      lastSessionId,
      materialLines,
      materialCommitIdempotencyKey,
      finalizedTimeLogId,
    );

    if (result.materialsConfirmed) {
      completeFinalizeUiTransition();
      return;
    }

    showMaterialRetryState();
  }

  /**
   * Finalize (work summary + durable material commit). Identical logic to
   * the original standalone handler; sessionId/timelogSnapshot are now
   * accepted explicitly so handleSubmitSession can chain this immediately
   * after its own Phase 1 write without waiting on React state to commit.
   * Called bare (no args) it falls back to lastSessionId/reviewTimelog state,
   * which is how the crash-recovery retry path (below) invokes it.
   */
  async function handleFinalizeSession(sessionIdArg, timelogArg) {
    devLog("SUBMIT CLICKED");
    const sessionId = sessionIdArg ?? lastSessionId;
    const timelogForFinalize = timelogArg ?? reviewTimelog;

    if (!sessionId) {
      alert("No session found.");
      setIsSubmittingSession(false);
      return;
    }

    await flushSessionMaterialSync(sessionId);

    const pendingLogs = getPendingTimeLogs(currentUser);
    const existingLog = pendingLogs.find((log) => log.session_id === sessionId);
    const materialLines = getSessionMaterials(sessionId);
    const materialCommitIdempotencyKey =
      existingLog?.material_commit_idempotency_key ||
      (materialLines.length ? crypto.randomUUID() : undefined);

    // Explicit choice, else org default; a recovery retry keeps the type the
    // stop write already carried. Never a generic label.
    const workTypeForFinalize =
      resolvedWorkType ||
      String(timelogForFinalize?.work_category || existingLog?.work_category || "").trim();

    const finalizedLog = {
      ...(timelogForFinalize || {}),
      work_completed: workCompleted,
      work_category: workTypeForFinalize,
      notes: notes,
      pending_materials: materialLines.length ? materialLines : undefined,
      materials_synced: false,
      material_commit_idempotency_key: materialCommitIdempotencyKey,
    };

    const existingIdx = pendingLogs.findIndex(
      (log) => log.session_id === sessionId,
    );

    const updatedLogs =
      existingIdx >= 0
        ? pendingLogs.map((log, index) =>
            index === existingIdx ? { ...log, ...finalizedLog } : log,
          )
        : [...pendingLogs, finalizedLog];

    savePendingTimeLogs(updatedLogs, currentUser);

    let timelogSynced = false;
    let labourTimeLogId = null;

    if (isOffline()) {
      traceTimelogWrite({
        operation: "update",
        path: FINALIZE_TRACE_PATH,
        sessionId,
        phase: "error",
        error: "offline",
      });
      devLog("OFFLINE FINALIZE SESSION");
    } else {
      try {
        const result = await runCriticalTimelogFinalize({
          sessionId,
          reviewTimelog: timelogForFinalize,
          workCompleted,
          category: workTypeForFinalize,
          notes,
          materialLines,
          getPendingTimeLogs,
          savePendingTimeLogs,
          currentUser,
        });
        timelogSynced = result.timelogSynced;
        labourTimeLogId = result.timeLogId || null;
        if (labourTimeLogId) {
          setFinalizedTimeLogId(labourTimeLogId);
        }
      } catch (criticalError) {
        console.error("FINALIZE CRITICAL ERROR:", criticalError);
        traceTimelogWrite({
          operation: "update",
          path: FINALIZE_TRACE_PATH,
          sessionId,
          phase: "error",
          error: criticalError,
        });
      }
    }

    if (isOffline()) {
      // Progress is not queued offline; report it as not saved (never a fake success).
      if (buildProgressReports(progressTasks, progressDrafts).reports.length) {
        setProgressOutcome({
          status: "offline",
          sessionId,
          reports: buildProgressReports(progressTasks, progressDrafts).reports,
          saved: [],
          failed: [],
        });
      }
      completeFinalizeUiTransition();
      return;
    }

    if (!timelogSynced) {
      // Online but the finalize update failed (transient network/RLS error).
      // Phase 1's stop write already succeeded, so the session cannot resume
      // — fall back to the same crash-recovery screen used for reload
      // -during-finalize, so the labour record is never stranded silently.
      setShowPostStopRecovery(true);
      setIsSubmittingSession(false);
      return;
    }

    // The labour time log is saved: report task progress in its own boundary.
    startTaskProgressSubmit(sessionId);

    void runOptionalFinalizeQuoteLink({
      sessionId,
      reviewTimelog: timelogForFinalize,
      workCompleted,
      category: workTypeForFinalize,
    });

    try {
      const materialResult = await persistFinalizeMaterials(
        sessionId,
        materialLines,
        materialCommitIdempotencyKey,
        labourTimeLogId,
        timelogForFinalize,
      );
      if (!materialResult.materialsConfirmed) {
        showMaterialRetryState();
        return;
      }
    } catch (materialError) {
      console.error("FINALIZE MATERIALS ERROR:", materialError);
      showMaterialRetryState();
      return;
    }

    completeFinalizeUiTransition();
  }

  /**
   * Submit Session — the single authoritative completion action. Composes
   * the same two proven pipelines that previously ran as separate user
   * actions (Stop-confirm, then Finalize-Submit): Phase 1 captures the
   * authoritative end time and performs the existing stop write (unchanged
   * from the previous handleStopSession); Phase 2 immediately runs the
   * existing finalize pipeline (work summary + durable material commit)
   * against the value just computed, rather than waiting on React state.
   * The session remains fully active (Active Session/timer/local cache all
   * intact) right up until this function begins.
   */
  async function handleSubmitSession() {
    if (!activeSession || isSubmittingSession) {
      return;
    }

    // Validate before Phase 1 so a blocked submit leaves the session running.
    if (!ensureWorkTypeForSubmit()) {
      return;
    }

    setIsSubmittingSession(true);

    const sessionId = activeSession.session_id;

    // ---- Phase 1: authoritative stop (unchanged from handleStopSession) ----
    await flushSessionMaterialSync(sessionId);

    const startTime = new Date(activeSession.session_start);
    const endTime = new Date();

    const totalSeconds = computeSessionDurationSeconds(startTime, endTime);

    const totalHours = Number(
      ((totalSeconds - (activeSession.break_seconds || 0)) / 3600).toFixed(2),
    );

    const employeeSnapshot = buildEmployeeSnapshot({
      supabase_user_id: activeSession.supabase_user_id,
      employee_name: currentUser?.employee_name || session?.user?.email || "Unknown",
      trade_id: activeSession.trade_id,
      trade_name: activeSession.trade_name,
      classification_name: activeSession.classification_name,
      payroll_type: activeSession.payroll_type,
      hourly_rate: activeSession.hourly_rate,
    });

    const timelogPayload = {
      session_id: sessionId,
      ...snapshotToTimelogFields(employeeSnapshot),
      project_id: toNullableUuid(activeSession.project_id),
      project_name: activeSession.project_name || "",
      client_id: toNullableUuid(activeSession.client_id),
      start_time: activeSession.session_start,
      date_iso: toLocalDateInputValue(activeSession.session_start),
      end_time: endTime.toISOString(),
      total_hours: totalHours,
      total_seconds: totalSeconds,
      break_seconds: activeSession.break_seconds || 0,
      net_work_seconds: totalSeconds - (activeSession.break_seconds || 0),
      // Carry the resolved Work Type from the first write so the queued and
      // synced row is never blank, even if finalize has to be retried.
      work_category: resolvedWorkType,
      source: "TimeTrackingV2",
    };

    const pending = getPendingTimeLogs(currentUser);
    const linkedTimelogPayload = stampPendingTimelogForQueue(
      isOffline()
        ? timelogPayload
        : await applyQuoteLineLinkToTimelogRow(timelogPayload),
      currentUser,
    );
    savePendingTimeLogs(
      upsertPendingTimelogBySessionId(pending, linkedTimelogPayload),
      currentUser,
    );

    const organizationId = resolveCacheOrganizationId(currentUser);
    enqueuePendingActiveSessionDelete(sessionId, organizationId);

    devLog("TIMELOG QUEUED LOCALLY:", linkedTimelogPayload);

    traceTimelogWrite({
      operation: "insert",
      path: "TimeTrackingV2Screen.handleSubmitSession",
      sessionId: linkedTimelogPayload.session_id,
      phase: "attempt",
    });

    if (!isOffline()) {
      const { error: insertError } = await orgScopedInsert(
        supabase,
        "time_logs",
        [linkedTimelogPayload],
        currentUser,
      );

      if (insertError) {
        traceTimelogWrite({
          operation: "insert",
          path: "TimeTrackingV2Screen.handleSubmitSession",
          sessionId: linkedTimelogPayload.session_id,
          phase: "error",
          error: insertError,
        });
        devLog("OFFLINE STOP SESSION — timelog queued, will sync later");
      } else {
        traceTimelogWrite({
          operation: "insert",
          path: "TimeTrackingV2Screen.handleSubmitSession",
          sessionId: linkedTimelogPayload.session_id,
          phase: "success",
        });
        const remainingPending = getPendingTimeLogs(currentUser).filter(
          (log) => log.session_id !== timelogPayload.session_id,
        );

        savePendingTimeLogs(remainingPending, currentUser);

        devLog("TIMELOG SAVED TO SUPABASE");
      }

      const { error: deleteError } = await supabase
        .from("active_sessions")
        .delete()
        .eq("session_id", sessionId);

      if (deleteError) {
        devLog("OFFLINE STOP SESSION — active_sessions delete deferred");
      } else {
        clearPendingActiveSessionDelete(sessionId, organizationId);
      }
    } else {
      traceTimelogWrite({
        operation: "insert",
        path: "TimeTrackingV2Screen.handleSubmitSession",
        sessionId: linkedTimelogPayload.session_id,
        phase: "error",
        error: "offline",
      });
      devLog("OFFLINE STOP SESSION — timelog queued, will sync later");
    }

    setLastSessionId(sessionId);

    saveReviewSession(timelogPayload);

    setReviewTimelog(timelogPayload);

    clearLocalActiveSession(activeSession.supabase_user_id);

    setActiveSession(null);
    dispatchSessionStateChanged({ action: SESSION_STATE_ACTION.STOP_SESSION });

    setElapsedSeconds(0);

    setShowCloseoutScreen(false);

    // ---- Phase 2: finalize (unchanged from handleFinalizeSession) ----
    // Pass the values just computed explicitly rather than relying on the
    // setReviewTimelog/setLastSessionId calls above having already
    // re-rendered — React state updates are not guaranteed to be visible
    // synchronously this far into an async function.
    await handleFinalizeSession(sessionId, timelogPayload);
  }

  /**
   * Crash/reload recovery only: Phase 1 already wrote the stop in a previous
   * Submit attempt, but Phase 2 (finalize) never confirmed. Retries Phase 2
   * against the restored reviewTimelog/lastSessionId state.
   */
  async function handleRetrySubmitFromRecovery() {
    if (isSubmittingSession || !reviewTimelog) {
      return;
    }

    if (
      !resolvedWorkType &&
      !String(reviewTimelog.work_category || "").trim() &&
      !ensureWorkTypeForSubmit()
    ) {
      return;
    }

    setIsSubmittingSession(true);
    await handleFinalizeSession(reviewTimelog.session_id, reviewTimelog);
  }
  if (materialRetryPending) {
    return (
      <div className="screen nx-screen ci-success-screen">
        <div className="ci-success-content">
          <h1 className="ci-success-title sw-success-title">
            Labour saved; materials still need to be submitted
          </h1>
          <p className="ci-success-sub sw-success-sub">
            Your time entry was recorded. Materials could not be saved — Retry.
            You can leave and retry later without re-entering the materials.
          </p>
          <button
            className="nx-btn nx-btn-gold"
            style={{ marginTop: "12px" }}
            onClick={handleRetryMaterialFinalize}
          >
            Retry
          </button>
          <button
            className="nx-btn sw-btn-secondary"
            style={{ marginTop: "12px" }}
            onClick={() => {
              clearReviewSession();
              setReviewTimelog(null);
              setMaterialRetryPending(false);
              onBack();
            }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }
  if (showSuccessScreen) {
    return (
      <div className="screen nx-screen ci-success-screen">
        <div className="ci-success-content">
          <div className="ci-success-icon">
            <svg
              viewBox="0 0 52 52"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="checkmark-circle nx-checkmark-circle"
                cx="26"
                cy="26"
                r="24"
              />
              <path
                className="checkmark-check nx-checkmark-check"
                d="M14.1 27.2l7.1 7.2 16.7-16.8"
              />
            </svg>
          </div>

          <h1 className="ci-success-title sw-success-title">Mission Complete</h1>

          <p className="ci-success-sub sw-success-sub">
            Your time entry has been recorded successfully.
          </p>

          <SessionTaskProgressNotice
            outcome={progressOutcome}
            onRetry={
              (progressOutcome.status === "failed" || progressOutcome.status === "offline") &&
              progressOutcome.reports?.length
                ? () => sendTaskProgress(progressOutcome.sessionId, progressOutcome.reports).catch(() => {})
                : null
            }
          />

          <button
            className="nx-btn nx-btn-gold"
            style={{ marginTop: "12px" }}
            onClick={onBack}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }
  if (showPostStopRecovery && reviewTimelog) {
    // Crash/reload recovery only (see the mount effect and the !timelogSynced
    // branch of handleFinalizeSession): the stop write already completed for
    // this session in a previous attempt, but finalize never confirmed. The
    // active session is genuinely gone by this point, so this screen only
    // offers finishing the finalize step — never a return to Active Session.
    return (
      <div className="screen nx-screen kpi-screen sw-screen">
        <div className="sw-page-header sw-header-row">
          <h1 className="kpi-header-title sw-page-title--gold">
            Finish Submitting Session
          </h1>
        </div>

        <p className="sw-page-subtitle">
          Your time was recorded. Complete these details to finish submitting
          it.
        </p>

        <div className="sw-icon-row sw-icon-row--field">
          <span className="sw-icon-row-icon" aria-hidden="true">
            {SW_ICON.project}
          </span>
          <div className="sw-icon-row-body">
            <div className="sw-icon-row-label">Project</div>
            <div className="sw-icon-row-value">
              {reviewTimelog.project_name}
            </div>
          </div>
        </div>

        <div className="sw-row-grid-2">
          <div className="sw-icon-row">
            <span className="sw-icon-row-icon" aria-hidden="true">
              {SW_ICON.calendar}
            </span>
            <div className="sw-icon-row-body">
              <div className="sw-icon-row-label">Start</div>
              <div className="sw-icon-row-value">
                {formatSessionDateTime(reviewTimelog.start_time)}
              </div>
            </div>
          </div>

          <div className="sw-icon-row">
            <span className="sw-icon-row-icon" aria-hidden="true">
              {SW_ICON.calendar}
            </span>
            <div className="sw-icon-row-body">
              <div className="sw-icon-row-label">End</div>
              <div className="sw-icon-row-value">
                {formatSessionDateTime(reviewTimelog.end_time)}
              </div>
            </div>
          </div>
        </div>

        <div className="sw-row-grid-2">
          <div className="sw-icon-row">
            <span className="sw-icon-row-icon" aria-hidden="true">
              {SW_ICON.break}
            </span>
            <div className="sw-icon-row-body">
              <div className="sw-icon-row-label">Break</div>
              <div className="sw-icon-row-value">
                {Math.floor((reviewTimelog.break_seconds || 0) / 60)} min
              </div>
            </div>
          </div>

          <div className="sw-icon-row sw-icon-row--hero">
            <span className="sw-icon-row-icon" aria-hidden="true">
              {SW_ICON.clock}
            </span>
            <div className="sw-icon-row-body">
              <div className="sw-icon-row-label">Total Hours</div>
              <div className="sw-icon-row-value sw-icon-row-value--emphasis sw-value-gold">
                {formatTotalHoursDisplay(reviewTimelog.total_hours)}
              </div>
            </div>
          </div>
        </div>

        <div className="sw-section">
          <div className="sw-materials-compact">
            <SessionMaterialsPanel
              sessionId={reviewTimelog.session_id}
              projectId={reviewTimelog.project_id}
              mode="review"
              usageDate={
                reviewTimelog.date_iso ||
                toLocalDateInputValue(reviewTimelog.start_time)
              }
              compact
            />
          </div>
        </div>

        <div className="kpi-section-label">Work Summary</div>
        <div className="kpi-card sw-section">
          <CPDropdown
            label="Work Type"
            value={resolvedWorkType || String(reviewTimelog.work_category || "")}
            onChange={handleWorkTypeChange}
            placeholder="Select Work Type"
            options={workTypeOptions}
          />
          {workTypeError ? (
            <p className="sw-work-type-error" role="alert">
              {workTypeError}
            </p>
          ) : null}

          <CPInput
            className="cp-input-group--compact"
            label="Work Completed"
            placeholder="What was completed?"
            value={workCompleted}
            onChange={setWorkCompleted}
          />

          <CPTextarea
            className="cp-input-group--compact"
            label="Notes"
            rows={4}
            placeholder="Additional notes..."
            value={notes}
            onChange={setNotes}
          />
        </div>

        <button
          type="button"
          className="nx-btn nx-btn-gold sw-btn-full sw-btn-icon guide-submit-session"
          disabled={isSubmittingSession}
          onClick={handleRetrySubmitFromRecovery}
        >
          <span className="sw-btn-icon-glyph" aria-hidden="true">
            {SW_ICON.resume}
          </span>
          Submit Session
        </button>
      </div>
    );
  }
  function formatDuration(seconds) {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  return (
    <VoiceGuidedFormProvider value={voiceMvp}>
    <div className={`screen nx-screen kpi-screen sw-screen${isDesktop ? " sw-screen--desk" : ""}`}>
      {isDesktop && onBack ? (
        <DesktopBackLink onClick={onBack} label="Dashboard" />
      ) : null}
      <div className="sw-page-header sw-header-row">
        <h1 className="kpi-header-title sw-page-title--gold">
          {activeSession
            ? showCloseoutScreen
              ? "Review Session"
              : "Active Session"
            : "Start Session"}
        </h1>
        {activeSession && (
          <div
            className={`sw-status-pill ${
              showCloseoutScreen
                ? "sw-status-pill--working"
                : activeSession.break_active
                  ? "sw-status-pill--break"
                  : "sw-status-pill--working"
            }`}
          >
            {showCloseoutScreen
              ? "Session Active"
              : activeSession.break_active
                ? "On Break"
                : "Working"}
          </div>
        )}
      </div>

      {!activeSession && (
        <p className="sw-page-subtitle">
          Select your project and confirm your details to start tracking
          time.
        </p>
      )}
      {activeSession && showCloseoutScreen && (
        <p className="sw-page-subtitle">
          Review your session, then submit when you're ready to complete it.
        </p>
      )}
      <DeskGrid on={isDesktop}>
        <DeskCol on={isDesktop}>
        {activeSession && (
          <>
            <div
              className={`sw-hero-card ${
                activeSession.break_active ? "sw-hero-card--break" : ""
              }`}
            >
              <span className="sw-hero-icon" aria-hidden="true">
                {SW_ICON.clock}
              </span>
              <div>
                <div className="sw-hero-label">Session Time</div>
                <div className="sw-hero-value">
                  {formatDuration(elapsedSeconds)}
                </div>
              </div>
            </div>

            <div
              className="sw-icon-row sw-icon-row--clickable"
              onClick={() => {
                if (showActiveProjectEditor) return;
                setEditedProjectId(activeSession.project_id);
                setShowActiveProjectEditor(true);
              }}
            >
              <span className="sw-icon-row-icon" aria-hidden="true">
                {SW_ICON.project}
              </span>
              <div className="sw-icon-row-body">
                <div className="sw-icon-row-label">Project</div>
                <div className="sw-icon-row-value">
                  {activeSession.project_name}
                </div>
              </div>
              {!showActiveProjectEditor && (
                <span className="sw-icon-row-chevron" aria-hidden="true">
                  {SW_ICON.chevron}
                </span>
              )}
            </div>

            {showActiveProjectEditor && (
              <div className="sw-section" onClick={(e) => e.stopPropagation()}>
                <CPDropdown
                  value={editedProjectId}
                  onChange={(value) => {
                    devLog("CURRENT editedProjectId", editedProjectId);
                    devLog("PROJECT CHANGED", value);
                    setEditedProjectId(value);
                    setTimeout(() => {
                      devLog("AFTER SET", value);
                    }, 100);
                  }}
                  placeholder="Select Project"
                  options={projects.map((project) => ({
                    value: project.project_id,
                    label: project.project_name,
                  }))}
                />

                <div className="sw-inline-actions">
                  <button
                    className="nx-btn nx-btn-gold"
                    onClick={async () => {
                      const selectedProject = projects.find(
                        (p) => p.project_id === editedProjectId,
                      );
                      devLog("EDITED PROJECT ID", editedProjectId);
                      devLog("SELECTED PROJECT", selectedProject);
                      if (!selectedProject) {
                        return;
                      }

                      const updatedSession = {
                        ...activeSession,

                        project_id: selectedProject.project_id,

                        project_name: selectedProject.project_name,

                        client_id: selectedProject.client_id,
                      };

                      setActiveSession(updatedSession);

                      saveLocalActiveSession(updatedSession);

                      await supabase
                        .from("active_sessions")
                        .update({
                          project_id: selectedProject.project_id,

                          project_name: selectedProject.project_name,

                          client_id: selectedProject.client_id,
                        })
                        .eq("session_id", activeSession.session_id);

                      setShowActiveProjectEditor(false);
                    }}
                  >
                    Save
                  </button>

                  <button
                    className="nx-btn sw-btn-secondary"
                    onClick={() => setShowActiveProjectEditor(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {activeProject?.clients?.client_name && (
              <div className="sw-icon-row">
                <span className="sw-icon-row-icon" aria-hidden="true">
                  {SW_ICON.client}
                </span>
                <div className="sw-icon-row-body">
                  <div className="sw-icon-row-label">Client</div>
                  <div className="sw-icon-row-value">
                    {activeProject.clients.client_name}
                  </div>
                </div>
              </div>
            )}

            <div className="sw-row-grid-2">
              <div
                className="sw-icon-row sw-icon-row--clickable"
                onClick={() => {
                  const start = new Date(activeSession.session_start);

                  setEditedStartDate(toLocalDateInputValue(start));
                  setEditedStartTime(formatTimeInputValue(start));

                  setShowStartTimeEditor(true);
                }}
              >
                <span className="sw-icon-row-icon" aria-hidden="true">
                  {SW_ICON.calendar}
                </span>
                <div className="sw-icon-row-body">
                  <div className="sw-icon-row-label">Started</div>
                  <div className="sw-icon-row-value">
                    {formatSessionDateTime(activeSession.session_start)}
                  </div>
                </div>
              </div>

              <div
                className="sw-icon-row sw-icon-row--clickable"
                onClick={() => {
                  setReviewBreakMinutes(
                    String(activeSession.total_break_minutes || 0),
                  );

                  setShowBreakEditor(true);
                }}
              >
                <span className="sw-icon-row-icon" aria-hidden="true">
                  {SW_ICON.break}
                </span>
                <div className="sw-icon-row-body">
                  <div className="sw-icon-row-label">Break</div>
                  <div className="sw-icon-row-value">
                    {activeSession.total_break_minutes || 0} min
                  </div>
                </div>
              </div>
            </div>

            {showStartTimeEditor && (
              <div className="sw-section">
                <div className="sw-datetime-stack">
                  <label className="sw-datetime-label" htmlFor="active-start-date">
                    Start Date
                  </label>
                  <input
                    id="active-start-date"
                    type="date"
                    value={editedStartDate}
                    onChange={(e) => setEditedStartDate(e.target.value)}
                    className="sw-field-input"
                  />
                  <label className="sw-datetime-label" htmlFor="active-start-time">
                    Start Time
                  </label>
                  <input
                    id="active-start-time"
                    type="time"
                    value={editedStartTime}
                    onChange={(e) => setEditedStartTime(e.target.value)}
                    className="sw-field-input"
                  />
                </div>

                <div className="sw-inline-actions">
                  <button
                    className="nx-btn nx-btn-gold"
                    onClick={handleSaveStartTime}
                  >
                    Save
                  </button>

                  <button
                    className="nx-btn sw-btn-secondary"
                    onClick={() => setShowStartTimeEditor(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {showBreakEditor && (
              <div className="sw-section">
                <input
                  type="number"
                  min="0"
                  value={reviewBreakMinutes}
                  onChange={(e) => setReviewBreakMinutes(e.target.value)}
                  className="sw-field-input"
                />

                <div className="sw-inline-actions">
                  <button
                    className="nx-btn nx-btn-gold"
                    onClick={async () => {
                      const breakMinutes = Number(reviewBreakMinutes || 0);

                      const updatedSession = {
                        ...activeSession,
                        total_break_minutes: breakMinutes,
                        break_seconds: breakMinutes * 60,
                      };

                      setActiveSession(updatedSession);

                      saveLocalActiveSession(updatedSession);

                      await supabase
                        .from("active_sessions")
                        .update({
                          total_break_minutes: breakMinutes,
                          break_seconds: breakMinutes * 60,
                        })
                        .eq("session_id", activeSession.session_id);

                      setShowBreakEditor(false);
                    }}
                  >
                    Save
                  </button>

                  <button
                    className="nx-btn sw-btn-secondary"
                    onClick={() => setShowBreakEditor(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        </DeskCol>

        <DeskCol on={isDesktop}>
        {activeSession && (
          <div className="sw-materials-compact">
            <SessionMaterialsPanel
              sessionId={activeSession.session_id}
              projectId={activeSession.project_id}
              mode="capture"
              usageDate={toLocalDateInputValue(activeSession.session_start)}
              initialOpenAddForm={openMaterialsPicker}
              compact
            />
          </div>
        )}

        {activeSession && showCloseoutScreen && (
          <>
            <div className="kpi-section-label">Work Summary</div>
            <div className="kpi-card sw-section">
              <CPDropdown
                label="Work Type"
                value={resolvedWorkType}
                onChange={handleWorkTypeChange}
                placeholder="Select Work Type"
                options={workTypeOptions}
              />
              {workTypeError ? (
                <p className="sw-work-type-error" role="alert">
                  {workTypeError}
                </p>
              ) : null}

              <CPInput
                className="cp-input-group--compact"
                label="Work Completed"
                placeholder="What was completed?"
                value={workCompleted}
                onChange={setWorkCompleted}
              />

              <CPTextarea
                className="cp-input-group--compact"
                label="Notes"
                rows={4}
                placeholder="Additional notes..."
                value={notes}
                onChange={setNotes}
              />
            </div>
            <SessionTaskProgressPanel
              actor={currentUser}
              projectId={toNullableUuid(activeSession.project_id)}
              sessionMinutes={Math.max(0, (elapsedSeconds - (activeSession.break_seconds || 0)) / 60)}
              offline={connectivityOffline}
              drafts={progressDrafts}
              onDraftsChange={setProgressDrafts}
              onTasksLoaded={setProgressTasks}
            />
          </>
        )}
        </DeskCol>

      {!activeSession && (
        <>
          <DeskCol on={isDesktop}>
          <div className="sw-group-label">
            <span aria-hidden="true">{SW_ICON.project}</span>
            Work Context
          </div>

          <div className="sw-icon-row sw-icon-row--field">
            <span className="sw-icon-row-icon" aria-hidden="true">
              {SW_ICON.project}
            </span>
            <div className="sw-icon-row-body">
              <CPDropdown
                label="Project"
                className="guide-select-project"
                voiceFieldId="project"
                voiceActive={voiceMvp.activeFieldId === "project"}
                value={selectedProjectId}
                onChange={(value) => {
                  setSelectedProjectId(value);
                  voiceMvp.notifyFieldChange("project", value);
                }}
                placeholder="Select Project"
                options={projectOptions}
              />
            </div>
          </div>

          {selectedProject?.clients && (
            <div className="sw-icon-row sw-client-card">
              <span className="sw-icon-row-icon" aria-hidden="true">
                {SW_ICON.client}
              </span>
              <div className="sw-icon-row-body">
                <div className="sw-icon-row-label">Client</div>
                <div className="sw-icon-row-value">
                  {selectedProject.clients.client_name || "N/A"}
                </div>

                <div className="sw-client-subrow-grid">
                  <div>
                    <div className="sw-icon-row-label">Contact</div>
                    <div className="sw-icon-row-value">
                      {selectedProject.clients.contact_name || "N/A"}
                    </div>
                  </div>
                  <div>
                    <div className="sw-icon-row-label">Phone</div>
                    <div className="sw-icon-row-value">
                      {selectedProject.clients.phone || "N/A"}
                    </div>
                  </div>
                </div>

                <div className="sw-client-note">
                  <div className="sw-icon-row-label">Notes</div>
                  <div className="sw-icon-row-value">
                    {selectedProject.clients.notes || "No notes"}
                  </div>
                </div>
              </div>
            </div>
          )}

          </DeskCol>

          <DeskCol on={isDesktop}>
          <div className="sw-group-label">
            <span aria-hidden="true">{SW_ICON.payroll}</span>
            Session Setup
          </div>

          <div className="sw-icon-row">
            <span className="sw-icon-row-icon" aria-hidden="true">
              {SW_ICON.classification}
            </span>
            <div className="sw-icon-row-body">
              <div className="sw-icon-row-label">Classification</div>
              <div className="sw-icon-row-value">
                {currentUser?.classification_name || "Not set"}
              </div>
            </div>
          </div>

          <div className="sw-row-grid-2 sw-row-grid-2--session-setup">
            <div className="sw-icon-row sw-icon-row--field guide-payroll-type">
              <span className="sw-icon-row-icon" aria-hidden="true">
                {SW_ICON.payroll}
              </span>
              <div className="sw-icon-row-body">
                <CPDropdown
                  label="Payroll Type"
                  voiceFieldId="payrollType"
                  voiceActive={voiceMvp.activeFieldId === "payrollType"}
                  value={selectedPayrollType}
                  onChange={(value) => {
                    setSelectedPayrollType(value);
                    voiceMvp.notifyFieldChange("payrollType", value);
                  }}
                  options={payrollTypeOptions}
                />
              </div>
            </div>

            <div className="sw-icon-row">
              <span className="sw-icon-row-icon" aria-hidden="true">
                {SW_ICON.rate}
              </span>
              <div className="sw-icon-row-body">
                <div className="sw-icon-row-label">Hourly Rate</div>
                <div className="sw-icon-row-value sw-value-gold">
                  {resolvedHourlyRate > 0
                    ? `${formatCurrency(resolvedHourlyRate)}/hr`
                    : selectedPayrollType === "Standard"
                      ? "Not set"
                      : resolvedHourlyRate === null
                        ? ""
                        : "Not set"}
                </div>
              </div>
            </div>
          </div>
          </DeskCol>
        </>
      )}
      </DeskGrid>
      <DeskCol on={isDesktop} className="ops-actionbar">
      {!activeSession ? (
        <button
          className="nx-btn nx-btn-gold guide-start-session sw-btn-full sw-btn-icon"
          onClick={handleStartSession}
        >
          <span className="sw-btn-icon-glyph" aria-hidden="true">
            {SW_ICON.resume}
          </span>
          Start Session
        </button>
      ) : showCloseoutScreen ? (
        <div className="sw-section">
          <div className="sw-actions-row">
            <button
              type="button"
              className="nx-btn sw-btn-secondary"
              disabled={isSubmittingSession}
              onClick={() => setShowCloseoutScreen(false)}
            >
              Return to Session
            </button>

            <button
              type="button"
              className="nx-btn nx-btn-gold guide-submit-session"
              disabled={isSubmittingSession}
              onClick={handleSubmitSession}
            >
              Submit Session
            </button>
          </div>
        </div>
      ) : (
        <div className="sw-section">
          <div className="sw-group-label">
            <span aria-hidden="true">{SW_ICON.actions}</span>
            Actions
          </div>
          <div className="kpi-chart-card sw-break-actions">
            <div className="sw-actions-row">
              {activeSession?.break_active ? (
                <button
                  className="nx-btn sw-btn-secondary sw-btn-icon"
                  onClick={handleStopBreak}
                >
                  <span className="sw-btn-icon-glyph" aria-hidden="true">
                    {SW_ICON.resume}
                  </span>
                  Resume Work
                </button>
              ) : (
                <button
                  className="nx-btn sw-btn-secondary sw-btn-icon"
                  onClick={handleStartBreak}
                >
                  <span className="sw-btn-icon-glyph" aria-hidden="true">
                    {SW_ICON.break}
                  </span>
                  Start Break
                </button>
              )}

              <button
                className="nx-btn sw-btn-stop sw-btn-icon guide-stop-session"
                onClick={() => setShowCloseoutScreen(true)}
              >
                <span className="sw-btn-icon-glyph" aria-hidden="true">
                  {SW_ICON.stop}
                </span>
                Stop Session
              </button>
            </div>
          </div>
        </div>
      )}
      </DeskCol>
      <VoiceGuidedFormOverlay
        isActive={voiceMvp.isActive}
        phase={voiceMvp.phase}
        statusMessage={voiceMvp.statusMessage}
        interactionUI={voiceMvp.interactionUI}
        onConfirmYes={voiceMvp.confirmStepYes}
        onConfirmChooseAgain={voiceMvp.confirmStepNo}
      />
    </div>
    </VoiceGuidedFormProvider>
  );
}
